import {useState} from "react";
import {Tag} from "antd";
import className from "classnames/bind";
import {FontAwesomeIcon} from "~/components";
import style from "../style/SeoCheck.module.scss";

const cn = className.bind(style);

// Icon theo trạng thái tiêu chí.
const STATUS = {
	pass: {icon: 'fa-solid fa-circle-check'},
	warn: {icon: 'fa-solid fa-triangle-exclamation'},
	fail: {icon: 'fa-solid fa-circle-xmark'},
};

// Màu badge theo cấp heading: chữ đậm + nền nhạt cùng tông.
const HEADING_COLORS = {
	1: {color: '#d4380d', bg: '#fff2e8'},
	2: {color: '#096dd9', bg: '#e6f4ff'},
	3: {color: '#389e0d', bg: '#f6ffed'},
	4: {color: '#531dab', bg: '#f9f0ff'},
	5: {color: '#c41d7f', bg: '#fff0f6'},
	6: {color: '#08979c', bg: '#e6fffb'},
};

function fileName(src) {
	try {
		const p = new URL(src).pathname;
		return p.split('/').filter(Boolean).pop() || src;
	} catch {
		return src;
	}
}

/**
 * Phần dẫn chứng của tiêu chí. Hỗ trợ nhiều "kind":
 *  - text: danh sách dòng
 *  - image: lưới thumbnail ảnh (thiếu alt)
 *  - headings: cây heading (badge cấp); click → highlight trên preview
 *  - links: danh sách liên kết (badge nội bộ/ngoài/nofollow)
 */
function CriteriaDetails({details, onHighlight}) {
	const [open, setOpen] = useState(false);

	if (!details?.items?.length) return null;

	const {kind, items, truncated} = details;

	return (
		<div className={cn('item-details')}>
			<button type="button" className={cn('details-toggle')} onClick={() => setOpen(!open)}>
				<FontAwesomeIcon icon={open ? 'fa-light fa-chevron-down' : 'fa-light fa-chevron-right'} />
				{open ? 'Ẩn chi tiết' : `Xem chi tiết (${items.length}${truncated ? '+' : ''})`}
			</button>

			{open && kind === 'headings' && (
				<div className={cn('hd-tree')}>
					{items.map((h, i) => {
						const c = HEADING_COLORS[h.level] || HEADING_COLORS[6];
						const clickable = typeof onHighlight === 'function';
						return (
							<div
								key={i}
								className={cn('hd-row', {clickable})}
								style={{paddingLeft: `${(h.level - 1) * 18}px`}}
								onClick={clickable ? () => onHighlight({text: h.text, selector: `h${h.level}`}) : undefined}
								title={clickable ? 'Bấm để xem vị trí trên trang' : undefined}
							>
								<span className={cn('hd-badge')} style={{color: c.color, background: c.bg}}>H{h.level}</span>
								<span className={cn('hd-text')}>{h.text}</span>
								{clickable && <FontAwesomeIcon icon="fa-light fa-location-crosshairs" className={cn('hd-go')} />}
							</div>
						);
					})}
				</div>
			)}

			{open && kind === 'links' && (
				<ul className={cn('lk-list')}>
					{items.map((l, i) => (
						<li key={i} className={cn('lk-row')}>
							<Tag color={l.type === 'internal' ? 'green' : 'blue'}>{l.type === 'internal' ? 'Nội bộ' : 'Ngoài'}</Tag>
							{l.nofollow && <Tag>nofollow</Tag>}
							<a href={l.url} target="_blank" rel="noreferrer">{l.url}</a>
						</li>
					))}
				</ul>
			)}

			{open && kind === 'image' && (
				<div className={cn('details-images')}>
					{items.map((src, i) => {
						const clickable = typeof onHighlight === 'function';
						return (
							<div
								key={i}
								className={cn('details-image', {clickable})}
								title={clickable ? 'Bấm để xem vị trí trên trang' : src}
								onClick={clickable ? () => onHighlight({selector: 'img', src}) : undefined}
							>
								<img src={src} alt="" loading="lazy" />
								<span>{fileName(src)}</span>
								<a
									className={cn('details-image-open')}
									href={src}
									target="_blank"
									rel="noreferrer"
									title="Mở ảnh"
									onClick={(e) => e.stopPropagation()}
								>
									<FontAwesomeIcon icon="fa-light fa-arrow-up-right-from-square" />
								</a>
							</div>
						);
					})}
				</div>
			)}

			{open && (kind === 'text' || !kind) && (
				<ul className={cn('details-list')}>
					{items.map((text, i) => <li key={i}>{text}</li>)}
				</ul>
			)}

			{open && truncated > 0 && <p className={cn('details-more')}>… và {truncated} mục nữa</p>}
		</div>
	);
}

/**
 * Một tiêu chí: icon + nhãn + điểm + nhận xét + gợi ý + chi tiết. Màu nền/viền theo
 * trạng thái (xanh/cam/đỏ) để nổi bật chỗ cần cải thiện.
 */
function CriteriaItem({item, onHighlight}) {

	const status = STATUS[item.status] || STATUS.warn;

	return (
		<div className={cn('item', item.status)}>
			<div className={cn('item-icon')}>
				<FontAwesomeIcon icon={status.icon} />
			</div>
			<div className={cn('item-body')}>
				<div className={cn('item-head')}>
					<span className={cn('item-label')}>{item.label}</span>
					<span className={cn('item-score')}>{item.score}/{item.max}</span>
				</div>
				{item.message && <p className={cn('item-message')}>{item.message}</p>}
				{item.recommendation && (
					<p className={cn('item-reco')}>
						<FontAwesomeIcon icon="fa-light fa-lightbulb" /> {item.recommendation}
					</p>
				)}
				<CriteriaDetails details={item.details} onHighlight={onHighlight} />
			</div>
		</div>
	);
}

export default CriteriaItem;
